import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { Script } from "../types";
import { formatDateTime, browserTimeZone, supportedTimeZones } from "../utils/datetime";

/* Scheduled/recurring script automations — list + create/enable/run-now/delete.
 * Lives as the "Schedules" tab on the Scripts page. */

type Sched = {
  id: number;
  name: string;
  enabled: boolean;
  script_id: number;
  script_name?: string;
  scope: string;
  customer_id?: number | null;
  schedule_type: string;
  run_at?: string | null;
  days_of_week: number[];
  start_time?: string | null;
  timezone?: string | null;
  last_run_at?: string | null;
};

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // index = 0..6 (Mon=0)

function cadence(s: Sched): string {
  if (s.schedule_type === "one_off") return s.run_at ? `Once · ${formatDateTime(s.run_at)}` : "Once";
  const days = (s.days_of_week || []).map((d) => DOW[d]).join(", ");
  return `${days || "—"} at ${s.start_time || "?"} ${s.timezone || ""}`;
}

function scopeLabel(s: Sched, custName: (id?: number | null) => string): string {
  if (s.scope === "global") return "All devices";
  if (s.scope === "customer") return custName(s.customer_id);
  return s.scope;
}

export default function ScriptSchedulesPanel({
  scripts,
  canManage,
}: {
  scripts: Script[];
  canManage: boolean;
}) {
  const [rows, setRows] = useState<Sched[] | null>(null);
  const [customers, setCustomers] = useState<{ id: number; name: string }[]>([]);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({
    name: "",
    script_id: 0,
    scope: "customer",
    customer_id: 0,
    schedule_type: "recurring",
    run_at: "",
    days_of_week: [] as number[],
    start_time: "02:00",
    timezone: browserTimeZone(),
    enabled: true,
  });

  const load = useCallback(() => {
    api.get("/api/v1/script-schedules").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  useEffect(() => {
    load();
    api.get("/api/v1/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
  }, [load]);

  const custName = (id?: number | null) =>
    customers.find((c) => c.id === id)?.name || (id ? `#${id}` : "—");

  const toggleDay = (d: number) =>
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter((x) => x !== d)
        : [...f.days_of_week, d].sort(),
    }));

  const create = async () => {
    setMsg("");
    const body: any = {
      name: form.name.trim() || "Scheduled script",
      script_id: form.script_id,
      scope: form.scope,
      schedule_type: form.schedule_type,
      enabled: form.enabled,
    };
    if (form.scope === "customer") body.customer_id = form.customer_id || null;
    if (form.schedule_type === "recurring") {
      body.days_of_week = form.days_of_week;
      body.start_time = form.start_time;
      body.timezone = form.timezone;
    } else {
      body.run_at = form.run_at ? new Date(form.run_at).toISOString() : null;
    }
    try {
      await api.post("/api/v1/script-schedules", body);
      setCreating(false);
      setForm((f) => ({ ...f, name: "", days_of_week: [] }));
      load();
    } catch (e: any) {
      setMsg(e.response?.data?.detail || "Could not create the schedule.");
    }
  };

  const toggle = async (s: Sched) => {
    await api.put(`/api/v1/script-schedules/${s.id}`, { ...s, enabled: !s.enabled }).catch(() => {});
    load();
  };
  const runNow = async (s: Sched) => {
    if (!window.confirm(`Run "${s.script_name}" now on ${scopeLabel(s, custName)} (online devices)?`)) return;
    const r = await api.post(`/api/v1/script-schedules/${s.id}/run-now`).catch(() => null);
    setMsg(r ? `Queued on ${r.data.queued} device(s).` : "Run failed.");
    setTimeout(() => setMsg(""), 6000);
  };
  const del = async (s: Sched) => {
    if (!window.confirm(`Delete schedule "${s.name}"?`)) return;
    await api.delete(`/api/v1/script-schedules/${s.id}`).catch(() => {});
    load();
  };

  const canSubmit =
    form.script_id > 0 &&
    (form.scope !== "customer" || form.customer_id > 0) &&
    (form.schedule_type === "one_off"
      ? !!form.run_at
      : form.days_of_week.length > 0 && !!form.start_time && !!form.timezone);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Run a library script automatically on a schedule (across all devices, or one customer's).
          Fires on in-scope <strong>online</strong> devices; every run is recorded in Run history.
        </p>
        {canManage && (
          <button
            onClick={() => setCreating((v) => !v)}
            className="shrink-0 text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700"
          >
            {creating ? "Cancel" : "+ New schedule"}
          </button>
        )}
      </div>

      {msg && <p className="text-sm text-blue-700 dark:text-blue-300">{msg}</p>}

      {creating && canManage && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-xs text-gray-500 mb-1">Name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nightly cleanup" className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm" />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500 mb-1">Script</span>
              <select value={form.script_id} onChange={(e) => setForm({ ...form, script_id: Number(e.target.value) })}
                className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm">
                <option value={0}>— pick a script —</option>
                {scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500 mb-1">Scope</span>
              <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
                className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm">
                <option value="customer">One customer</option>
                <option value="global">All devices</option>
              </select>
            </label>
            {form.scope === "customer" && (
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1">Customer</span>
                <select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: Number(e.target.value) })}
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm">
                  <option value={0}>— pick a customer —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            )}
            <label className="text-sm">
              <span className="block text-xs text-gray-500 mb-1">Cadence</span>
              <select value={form.schedule_type} onChange={(e) => setForm({ ...form, schedule_type: e.target.value })}
                className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm">
                <option value="recurring">Recurring (weekly)</option>
                <option value="one_off">Once</option>
              </select>
            </label>
          </div>

          {form.schedule_type === "recurring" ? (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex gap-1">
                {DOW.map((d, i) => (
                  <button key={d} onClick={() => toggleDay(i)}
                    className={`text-xs px-2 py-1 rounded ${form.days_of_week.includes(i) ? "bg-blue-600 text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"}`}>
                    {d}
                  </button>
                ))}
              </div>
              <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm" />
              <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm max-w-[16rem]">
                {supportedTimeZones().map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
              </select>
            </div>
          ) : (
            <input type="datetime-local" value={form.run_at} onChange={(e) => setForm({ ...form, run_at: e.target.value })}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm" />
          )}

          <div className="flex justify-end">
            <button onClick={create} disabled={!canSubmit}
              className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              Create schedule
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Script</th>
              <th className="px-4 py-2 text-left">Scope</th>
              <th className="px-4 py-2 text-left">Cadence</th>
              <th className="px-4 py-2 text-left">Last run</th>
              <th className="px-4 py-2 text-left">Enabled</th>
              {canManage && <th className="px-4 py-2"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-gray-700 dark:text-gray-300">
            {rows && rows.length === 0 && (
              <tr><td colSpan={canManage ? 7 : 6} className="px-4 py-6 text-center text-gray-400">No schedules yet.</td></tr>
            )}
            {(rows || []).map((s) => (
              <tr key={s.id}>
                <td className="px-4 py-2 font-medium">{s.name}</td>
                <td className="px-4 py-2">{s.script_name || `#${s.script_id}`}</td>
                <td className="px-4 py-2">{scopeLabel(s, custName)}</td>
                <td className="px-4 py-2 text-xs">{cadence(s)}</td>
                <td className="px-4 py-2 text-xs text-gray-400">{s.last_run_at ? formatDateTime(s.last_run_at) : "never"}</td>
                <td className="px-4 py-2">
                  {canManage ? (
                    <button onClick={() => toggle(s)}
                      className={`inline-flex h-5 w-9 items-center rounded-full px-0.5 transition ${s.enabled ? "bg-green-500 justify-end" : "bg-gray-300 dark:bg-gray-600 justify-start"}`}>
                      <span className="h-4 w-4 rounded-full bg-white" />
                    </button>
                  ) : (
                    <span className={s.enabled ? "text-green-600" : "text-gray-400"}>{s.enabled ? "On" : "Off"}</span>
                  )}
                </td>
                {canManage && (
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => runNow(s)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline mr-3">Run now</button>
                    <button onClick={() => del(s)} className="text-xs text-rose-600 dark:text-rose-400 hover:underline">Delete</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
